import type { FrameworkIconKey } from "@repo/shared";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRightLeft,
  Axis3d,
  Blocks,
  BookOpen,
  Bot,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  CalendarRange,
  ChartColumn,
  Clock3,
  Columns3,
  Database,
  FileText,
  FolderKanban,
  FolderTree,
  Funnel,
  Gauge,
  GitBranch,
  Hash,
  HelpCircle,
  Layers,
  Layers3,
  LayoutDashboard,
  Link2,
  LogIn,
  MapPin,
  MessageSquareText,
  MonitorPlay,
  Network,
  Package,
  PanelsTopLeft,
  Pyramid,
  Shapes,
  SquareActivity,
  SquareArrowRightEnter,
  Table,
  Table2,
  Tag,
  Tags,
  Target,
  TextCursorInput,
  Timer,
  User,
  Users,
  Wrench,
} from "lucide-react";

/**
 * Key → Lucide component map. `satisfies Record<FrameworkIconKey, LucideIcon>`
 * is the single-vocabulary guard: the shared `FRAMEWORK_ICON_KEYS` catalog is the
 * source of truth, and any key added there (or removed) without a matching entry
 * here is a compile error — no hand-synced drift, no silent fallback.
 */
const FRAMEWORK_ICON_MAP = {
  ArrowRightLeft,
  Axis3d,
  BookOpen,
  Bot,
  Blocks,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  CalendarRange,
  ChartColumn,
  Clock3,
  Columns3,
  Database,
  FileText,
  FolderKanban,
  FolderTree,
  Funnel,
  Gauge,
  GitBranch,
  Hash,
  HelpCircle,
  Layers,
  Layers3,
  LayoutDashboard,
  Link2,
  LogIn,
  MapPin,
  MessageSquareText,
  MonitorPlay,
  Network,
  Package,
  PanelsTopLeft,
  Pyramid,
  Shapes,
  SquareActivity,
  SquareArrowRightEnter,
  Table,
  Table2,
  Tag,
  Tags,
  Target,
  TextCursorInput,
  Timer,
  User,
  Users,
  Wrench,
} satisfies Record<FrameworkIconKey, LucideIcon>;

/** All framework icon keys paired with their Lucide component, for pickers/grids. */
export const FRAMEWORK_ICON_ENTRIES = Object.entries(FRAMEWORK_ICON_MAP) as ReadonlyArray<
  [FrameworkIconKey, LucideIcon]
>;

export function resolveFrameworkIcon(
  iconKey: string | null | undefined,
  fallback: React.ElementType
): React.ElementType {
  if (!iconKey) return fallback;
  return FRAMEWORK_ICON_MAP[iconKey as keyof typeof FRAMEWORK_ICON_MAP] ?? fallback;
}
